import { Stack, Typography, Grid } from "@mui/material";
import Image from "next/image";
import theme from "@/theme/theme";
import QrCard from "./qrCard";
import QrFlow from "./qrFlow";

import zkAndPqcIcon from "@/assets/images/servicesPage/zkIcon.webp";

const FEATURES = [
    {
        title: "Language-independent",
        description: "Agents reason about cryptographic usage in any language, framework or configuration format. There are no per-language rule packs to maintain.",
    },
    {
        title: "No data leaves your organisation",
        description: "The framework installs and runs entirely inside your own infrastructure. Source code, keys and findings never leave your perimeter.",
    },
    {
        title: "Modular access levels",
        description: "You decide what the agents may touch: read-only analysis per repository, or read/write capabilities for assisted remediation.",
    },
    {
        title: "Crypto Bill of Materials",
        description: "A full inventory across OS, middleware, networks, software, libraries, plugins and hardware components, with your critical chains identified.",
    },
    {
        title: "Concrete, classified findings",
        description: "Deprecated and quantum-vulnerable algorithms (RSA, ECDSA, DH), hardcoded keys, certificates and their expiry. Every finding is classified classical or quantum-safe.",
    },
    {
        title: "Re-runnable, anytime",
        description: "Run the scan on demand or on a schedule. Each report tracks progress against the previous one, turning compliance into a continuous process.",
    },
];

const ROADMAP_ITEMS = [
    {
        title: "Automated certificate & key renewal",
        description: "Crypto agility built in: request, replace and roll over certificates and key material automatically as standards and lifetimes change.",
    },
    {
        title: "Generated migration roadmaps",
        description: "Turn the inventory into a prioritised, supplier-aware migration plan: risk-based, value-based or outside-in.",
    },
    {
        title: "Binary & firmware analysis",
        description: "Verify what actually ships, not just what source code intends, by surfacing vendor-supplied and compiled-in cryptography that repo scans can't see.",
    },
    {
        title: "An extensible agent network",
        description: "The same framework generalises to other scanning domains: security audits, bug detection, dependency health and beyond.",
    },
];

const QrFramework = () => {
    return (
        <Stack
            width={{ xs: "100%", lg: "80%" }}
            marginX="auto"
            paddingX={4}
            gap={6}
            position="relative"
        >
            <Stack
                direction={{ xs: "column", lg: "row" }}
                alignItems="start"
                justifyContent="center"
                gap={6}
            >
                <Stack width={{ xs: "100%", lg: "40%" }} gap={4}>
                    <Stack gap={2}>
                        <Stack direction={"row"} alignItems={"center"} gap={1.5}>
                            <Image src={zkAndPqcIcon} alt="quantum readiness framework icon" height={30} width={30} style={{ height: "auto", width: "auto" }} />
                            <Typography variant="h3" component="h3" className="chno" fontWeight={500} textTransform="uppercase" marginBottom={0.5} fontSize={{ xs: "1.5rem !important", lg: "2.25rem !important" }}>
                                The framework
                            </Typography>
                        </Stack>
                        <Typography variant="body2" component="p" fontSize="0.95rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                            We built an LLM-based agent framework that works <strong style={{ color: theme.palette.text.primary }}>with</strong> your
                            organisation on PQC compliance. It integrates with your repositories on GitHub or
                            any other host, scans your landscape and reports where you stand.
                        </Typography>
                        <Typography variant="body2" component="p" fontSize="0.95rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                            Unlike pattern-matching scanners, the agents understand context: how a key is used,
                            which chain depends on it, and whether a finding actually matters. The result is not
                            a pile of matches but a Crypto Bill of Materials you can plan a migration on.
                        </Typography>
                    </Stack>
                </Stack>
                <Stack width={{ xs: "100%", lg: "60%" }} gap={4}>
                    <Grid container spacing={3}>
                        {FEATURES.map((feature) => (
                            <Grid key={feature.title} size={{ xs: 12, sm: 6 }}>
                                <Stack gap={1}>
                                    <Typography variant="h6" component="h6" fontWeight={400}>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body2" component="p" fontSize="0.85rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                                        {feature.description}
                                    </Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Stack>
            <QrFlow />
            <Stack gap={3}>
                <Typography variant="h6" component="h6" fontWeight={400}>
                    Where this is heading
                </Typography>
                <Grid container spacing={3}>
                    {ROADMAP_ITEMS.map((item) => (
                        <Grid key={item.title} size={{ xs: 12, sm: 6, lg: 3 }}>
                            <QrCard>
                                <Stack gap={1}>
                                    <Typography variant="h6" component="h6" fontWeight={400} fontSize="1rem !important">
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" component="p" fontSize="0.85rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                                        {item.description}
                                    </Typography>
                                </Stack>
                            </QrCard>
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        </Stack>
    );
};

export default QrFramework;
